import React, { forwardRef, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import className from 'classnames/bind';

import { CoreIcon } from '@autopass-web/mochi-icons';

import styles from './mochi-modal.module.scss';

export interface MochiModalHeaderProps {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const MochiModalHeader = ({
  children,
  className,
  style,
}: MochiModalHeaderProps) => {
  return (
    <header className={cx('header', className)} style={style}>
      {children}
    </header>
  );
};

export interface MochiModalBodyProps {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const MochiModalBody = ({
  children,
  className,
  style,
}: MochiModalBodyProps) => {
  return (
    <div className={cx('body', className)} style={style}>
      {children}
    </div>
  );
};

export interface MochiModalFooterProps {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

export const MochiModalFooter = ({
  children,
  className,
  style,
}: MochiModalFooterProps) => {
  return (
    <div className={cx('footer', className)} style={style}>
      {children}
    </div>
  );
};

export interface MochiModalProps {
  isOpen: boolean;
  closeable?: boolean;
  isDarkMode?: boolean;
  contentClassName?: string;
  children: React.ReactNode;
  variant?: 'default' | 'full';
  onClose?: () => void;
}

const cx = className.bind(styles);

export const MochiModal = forwardRef<HTMLDivElement, MochiModalProps>(
  (props, ref) => {
    const {
      isOpen = false,
      closeable = false,
      variant = 'default',
      isDarkMode,
      contentClassName: _contentClassName,
      children,
      onClose,
    } = props;

    const selectorRef = useRef<HTMLElement | null>(null);

    const dialogLayoutClasses = cx({
      'dialog-layout': true,
      show: isOpen,
    });

    const contentClassName = cx(
      {
        content: true,
        [variant]: true,
        'light-mode': !isDarkMode,
        'dark-mode': isDarkMode,
      },
      _contentClassName,
    );

    useEffect(() => {
      selectorRef.current = document.body;
    }, []);

    if (selectorRef.current == null) return null;

    return createPortal(
      <div ref={ref} className={dialogLayoutClasses} onClick={onClose}>
        <div
          className={`${contentClassName}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {closeable && (
            <CoreIcon
              src="/assets/close.svg"
              onClick={onClose}
              className={styles.close}
            />
          )}
          {children}
        </div>
      </div>,
      selectorRef.current,
      'modal',
    );
  },
);

export const MochiModalV2 = {
  Root: MochiModal,
  Header: MochiModalHeader,
  Body: MochiModalBody,
  Footer: MochiModalFooter,
};
